import React, { useState } from 'react';
import { Truck, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './ui/LoadingSpinner';

export default function ForgotPasswordPage({ onBackToLogin }) {
  const { resetPassword, error } = useAuth();
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }

    setSending(true);
    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err) {
      setLocalError(err.message || 'Failed to send reset link. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const displayError = localError || error;

  if (success) {
    return (
      <div className="min-h-screen bg-locate2-bg-light flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-status-success-bg rounded-full flex items-center justify-center">
              <CheckCircle size={32} className="text-status-success" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-locate2-dark-blue mb-4">
            Check your email
          </h1>
          <p className="text-locate2-light-gray mb-6">
            We've sent a password reset link to <strong className="text-locate2-dark-blue">{email}</strong>.
            Please check your email and click the link to reset your password.
          </p>
          <button
            onClick={onBackToLogin}
            className="w-full bg-locate2-primary text-white rounded-lg px-4 py-3 font-medium hover:bg-locate2-dark-blue transition-all"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-locate2-bg-light flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <button
          onClick={onBackToLogin}
          className="flex items-center gap-2 text-locate2-light-gray hover:text-locate2-dark-blue transition-colors mb-6"
        >
          <ArrowLeft size={18} />
          Back to sign in
        </button>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 bg-locate2-primary rounded-lg flex items-center justify-center">
              <Truck size={24} className="text-white" />
            </div>
            <span className="text-2xl font-bold text-locate2-dark-blue">Locate2u</span>
          </div>
          <h1 className="text-2xl font-bold text-locate2-dark-blue mb-2">
            Forgot Password?
          </h1>
          <p className="text-locate2-light-gray">
            Enter your email and we'll send you a link to reset your password
          </p>
        </div>

        {displayError && (
          <div className="mb-4 p-3 bg-status-error-bg text-status-error rounded-lg text-sm">
            {displayError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-locate2-medium-gray mb-1">
              Email
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-locate2-light-gray" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-locate2-border rounded-lg focus:border-locate2-primary focus:outline-none transition-colors"
                placeholder="Enter your email"
                disabled={sending}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 bg-locate2-primary text-white rounded-lg px-4 py-3 font-medium hover:bg-locate2-dark-blue transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? <LoadingSpinner size="sm" className="border-white border-t-transparent" /> : null}
            {sending ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
      </div>
    </div>
  );
}
